import { SparklesIcon } from '@heroicons/react/outline';
import { type FormEvent, useId, useState } from 'react';

import clsxm from '@/lib/clsxm';
import { fitToLength, insertLyricSection } from '@/lib/musicStyle';

import {
  ArrowGlyph,
  buttonPrimary,
  tone,
} from '@/components/agents/primitives';

import { getMusicPreset, type MusicPreset } from '@/constants/musicPresets';

import { useMusicT, useSongIdea, useStartSong } from './kit';
import type { MusicLandingPage } from './types';

const LYRIC_TAGS = ['Verse', 'Chorus', 'Bridge', 'Outro'];

/**
 * The hero's song box. Most pages take a song description; the lyrics page
 * takes lyrics and a style (Custom Mode in the generator). Either way the
 * text goes through sign-up into the music generator, filled in and ready.
 */
export default function SongBox({ page }: { page: MusicLandingPage }) {
  return page.mode === 'lyrics' ? (
    <LyricsBox page={page} />
  ) : (
    <DescribeBox page={page} />
  );
}

function DescribeBox({ page }: { page: MusicLandingPage }) {
  const { t } = useMusicT();
  const id = useId();
  const { idea, setIdea, instrumental, setInstrumental, limits, boxRef } =
    useSongIdea();
  const { start, busy } = useStartSong(page.path);
  const [nextIdea, setNextIdea] = useState(0);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    // An empty box makes the example song, so one click is enough to start.
    const prompt = idea.trim() || fitToLength(t(page.placeholder), limits.idea);
    start({ prompt, instrumental }, 'hero');
  };

  // Cycles through the page's example ideas.
  const inspire = () => {
    if (page.ideas.length === 0) return;
    const item = page.ideas[nextIdea % page.ideas.length];
    setIdea(fitToLength(t(item.text), limits.idea));
    setInstrumental(Boolean(item.instrumental));
    setNextIdea((index) => index + 1);
    boxRef.current?.focus();
  };

  return (
    <form onSubmit={submit} className='mt-9'>
      <div className='rounded-[24px] bg-white p-2 shadow-[0_28px_70px_-34px_rgba(40,30,120,0.5)] ring-1 ring-[#0f0f28]/[0.08] transition-shadow focus-within:ring-2 focus-within:ring-primary-500 dark:bg-[#14141f] dark:ring-white/10'>
        <label htmlFor={id} className='sr-only'>
          {t('Describe your song')}
        </label>
        <textarea
          id={id}
          ref={boxRef}
          rows={3}
          value={idea}
          maxLength={limits.idea}
          onChange={(event) => setIdea(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.currentTarget.form?.requestSubmit();
            }
          }}
          placeholder={t(page.placeholder)}
          className={clsxm(
            'block w-full resize-none border-0 bg-transparent px-4 pb-2 pt-3.5 text-[17px] leading-7 placeholder:text-[#9a9ab0] focus:ring-0 dark:placeholder:text-[#6e6e8c]',
            tone.ink,
          )}
        />
        <div className='flex flex-wrap items-center gap-2 px-2 pb-1 pt-1'>
          <VocalsToggle
            instrumental={instrumental}
            onChange={setInstrumental}
          />
          {page.ideas.length > 0 && (
            <button
              type='button'
              onClick={inspire}
              className='inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-semibold text-[#56566d] transition-colors hover:bg-[#f2f2f8] hover:text-[#10101c] dark:text-[#a4a4bd] dark:hover:bg-white/[0.06] dark:hover:text-white'
            >
              <SparklesIcon className='h-4 w-4' aria-hidden='true' />
              {t('Inspire me')}
            </button>
          )}
          <div className='ms-auto flex items-center gap-2 max-sm:w-full'>
            <span
              className={clsxm(
                'px-1 text-xs tabular-nums max-sm:me-auto',
                idea.length >= limits.idea ? 'text-amber-600' : tone.quiet,
              )}
            >
              {idea.length}/{limits.idea}
            </span>
            <button
              type='submit'
              disabled={busy}
              className={clsxm(
                buttonPrimary,
                'px-5 py-3 text-[15px] disabled:cursor-wait disabled:opacity-80',
              )}
            >
              {t('Create song')}
              <ArrowGlyph />
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

function LyricsBox({ page }: { page: MusicLandingPage }) {
  const { t } = useMusicT();
  const lyricsId = useId();
  const styleId = useId();
  const { lyrics, setLyrics, style, setStyle, limits, boxRef } = useSongIdea();
  const { start, busy } = useStartSong(page.path);
  const [missing, setMissing] = useState(false);

  const sounds = page.sounds
    .map((soundId) => getMusicPreset(soundId))
    .filter((preset): preset is MusicPreset => Boolean(preset));

  const addTag = (tag: string) => {
    const box = boxRef.current;
    const from = box?.selectionStart ?? lyrics.length;
    const to = box?.selectionEnd ?? lyrics.length;
    const result = insertLyricSection(lyrics, from, to, tag);
    if (result.text.length > limits.lyrics) return;
    setLyrics(result.text);
    window.setTimeout(() => {
      box?.focus();
      box?.setSelectionRange(result.caret, result.caret);
    }, 0);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!lyrics.trim()) {
      setMissing(true);
      boxRef.current?.focus();
      return;
    }
    start(
      {
        lyrics,
        style: style.trim() || undefined,
        custom: true,
        instrumental: false,
      },
      'hero',
    );
  };

  return (
    <form onSubmit={submit} className='mt-9'>
      <div className='overflow-hidden rounded-[24px] bg-white shadow-[0_28px_70px_-34px_rgba(40,30,120,0.5)] ring-1 ring-[#0f0f28]/[0.08] transition-shadow focus-within:ring-2 focus-within:ring-primary-500 dark:bg-[#14141f] dark:ring-white/10'>
        <div
          role='toolbar'
          aria-label={t('Add a section tag')}
          className='flex flex-wrap items-center gap-1.5 border-b border-[#0f0f28]/[0.06] px-4 py-2.5 dark:border-white/[0.08]'
        >
          <span className={clsxm('me-1 text-xs font-medium', tone.quiet)}>
            {t('Add a section:')}
          </span>
          {LYRIC_TAGS.map((tag) => (
            <button
              key={tag}
              type='button'
              onClick={() => addTag(tag)}
              className='rounded-md bg-[#f2f2f8] px-2 py-1 font-mono text-xs text-[#4a4a63] transition-colors hover:bg-primary-50 hover:text-primary-700 dark:bg-white/[0.06] dark:text-[#b7b7d2] dark:hover:text-primary-300'
            >
              [{tag}]
            </button>
          ))}
        </div>
        <label htmlFor={lyricsId} className='sr-only'>
          {t('Your lyrics')}
        </label>
        <textarea
          id={lyricsId}
          ref={boxRef}
          rows={7}
          value={lyrics}
          maxLength={limits.lyrics}
          onChange={(event) => {
            setLyrics(event.target.value);
            if (missing) setMissing(false);
          }}
          placeholder={t(page.placeholder)}
          aria-invalid={missing || undefined}
          className={clsxm(
            'block w-full resize-y border-0 bg-transparent px-5 py-4 text-[16px] leading-7 placeholder:text-[#a3a3b8] focus:ring-0 dark:placeholder:text-[#6e6e8c]',
            tone.ink,
          )}
        />
        <div className='border-t border-[#0f0f28]/[0.06] px-2 py-2 dark:border-white/[0.08]'>
          <label htmlFor={styleId} className='sr-only'>
            {t('Style')}
          </label>
          <div className='flex flex-wrap items-center gap-2'>
            <input
              id={styleId}
              value={style}
              maxLength={limits.style}
              onChange={(event) => setStyle(event.target.value)}
              placeholder={t('Style: genre, mood, instruments, voice')}
              className={clsxm(
                'min-w-0 flex-1 basis-56 rounded-xl border-0 bg-[#f6f6fa] px-3.5 py-2.5 text-[15px] placeholder:text-[#9a9ab0] focus:ring-2 focus:ring-primary-500 dark:bg-white/[0.05] dark:placeholder:text-[#6e6e8c]',
                tone.ink,
              )}
            />
            <span
              className={clsxm(
                'px-1 text-xs tabular-nums',
                lyrics.length >= limits.lyrics ? 'text-amber-600' : tone.quiet,
              )}
            >
              {lyrics.length}/{limits.lyrics}
            </span>
            <button
              type='submit'
              disabled={busy}
              className={clsxm(
                buttonPrimary,
                'px-5 py-3 text-[15px] disabled:cursor-wait disabled:opacity-80',
              )}
            >
              {t('Turn into a song')}
              <ArrowGlyph />
            </button>
          </div>
        </div>
      </div>
      {missing && (
        <p
          role='alert'
          className='mt-3 text-sm font-medium text-amber-700 dark:text-amber-400'
        >
          {t('Paste or type your lyrics first.')}
        </p>
      )}

      {sounds.length > 0 && (
        <div className='mt-4 flex flex-wrap items-center gap-2'>
          <span className={clsxm('text-sm font-medium', tone.quiet)}>
            {t('Pick a sound:')}
          </span>
          {sounds.map((preset) => {
            const selected = style.trim() === preset.style;
            return (
              <button
                key={preset.id}
                type='button'
                aria-pressed={selected}
                onClick={() =>
                  setStyle(fitToLength(preset.style, limits.style))
                }
                className={clsxm(
                  'rounded-full px-3.5 py-1.5 text-sm ring-1 transition',
                  selected
                    ? 'bg-primary-600 text-white ring-primary-600'
                    : clsxm(
                        'bg-white shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-[#0f0f28]/[0.08] hover:text-primary-700 hover:ring-primary-300 dark:bg-white/[0.05] dark:ring-white/10 dark:hover:text-primary-300',
                        tone.body,
                      ),
                )}
              >
                {t(preset.title)}
              </button>
            );
          })}
        </div>
      )}
    </form>
  );
}

function VocalsToggle({
  instrumental,
  onChange,
}: {
  instrumental: boolean;
  onChange: (value: boolean) => void;
}) {
  const { t } = useMusicT();
  const options = [
    { value: false, label: t('With vocals') },
    { value: true, label: t('Instrumental') },
  ];
  return (
    <div
      role='group'
      aria-label={t('Vocals')}
      className='inline-flex rounded-full bg-[#f2f2f8] p-1 dark:bg-white/[0.06]'
    >
      {options.map((option) => {
        const active = option.value === instrumental;
        return (
          <button
            key={String(option.value)}
            type='button'
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={clsxm(
              'rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors',
              active
                ? 'bg-white text-[#10101c] shadow-[0_1px_3px_rgba(15,15,40,0.12)] dark:bg-white/15 dark:text-white'
                : 'text-[#6f6f86] hover:text-[#10101c] dark:text-[#9a9ab6] dark:hover:text-white',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
